import { dataType, NativeQSenseControl, NativeQSenseControlPlugin } from '@/plugins/NativeQSenseControl';

interface BaseSensorData {
  id: string;
  batteryLevel: number;
}

export interface SensorData extends BaseSensorData {
  movementHistory: number[];
}

type SensorUpdateCallback = (sensorId: string, data: SensorData) => void;

/**
 * Manages scanning, connection, and streaming of sensor data from devices.
 * Maintains event listeners and invokes callbacks when sensor updates are received.
 *
 * * Note: This is a mock implementation aimed at demonstrating the abstraction layer.
 * * Logic and data flow will be refined in subsequent versions.
 */
class SensorService {
  /**
   * In-memory store for sensor data keyed by sensor ID.
   * @private
   * @type {Record<string, SensorData>}
   */
  private sensors: Record<string, SensorData> = {};

  /**
   * Collection of listeners (callbacks) keyed by sensor ID.
   * @private
   * @type {Record<string, SensorUpdateCallback[]>}
   */
  private listeners: Record<string, SensorUpdateCallback[]> = {};

  /**
   * Indicates whether data streaming is currently active.
   * @private
   * @type {boolean}
   */
  private isStreaming = false;

  /**
   * @constructor
   * @param {NativeQSenseControlPlugin} plugin - A plugin instance for Q-Sense device interactions.
   */
  constructor(private plugin: NativeQSenseControlPlugin) {}

  /**
   * Initiates a scan for compatible devices.
   * @async
   * @param allowedDevices - A list of device MAC addresses or IDs to scan for.
   * @returns Resolves once scanning has started.
   */
  async startScan(allowedDevices: string[]): Promise<{ status: string }> {
    return this.plugin.startScan({ allowedDevices });
  }

  /**
   * Stops any ongoing device scan.
   * @async
   * @returns Resolves once scanning has stopped.
   */
  async stopScan(): Promise<{ status: string }> {
    return this.plugin.stopScan();
  }

  /**
   * Connects to all discovered devices.
   * @async
   * @returns Resolves once connections have been established.
   */
  async connectDevices(): Promise<void> {
    return this.plugin.connectDevices();
  }

  /**
   * Disconnects from all previously connected devices.
   * @async
   * @returns Resolves once all devices have been disconnected.
   */
  async disconnectDevices(): Promise<void> {
    return this.plugin.disConnectDevices();
  }

  /**
   * Subscribes a callback to receive streaming updates for a specific sensor ID.
   * When the first subscription is added, streaming is automatically started.
   * @async
   * @param sensorId - The sensor identifier.
   * @param callback - The function to call upon data updates.
   */
  async subscribe(sensorId: string, callback: SensorUpdateCallback): Promise<void> {
    if (!this.listeners[sensorId]) {
      this.listeners[sensorId] = [];
    }

    this.listeners[sensorId].push(callback);

    if (!this.isStreaming) {
      this.startStreaming();
    }
  }

  /**
   * Unsubscribes a specific callback from a given sensor ID.
   * If no more subscribers remain for that sensor, it is removed from tracking.
   * @param sensorId - The sensor identifier.
   * @param callback - The function to remove.
   */
  unsubscribe(sensorId: string, callback: SensorUpdateCallback) {
    if (!this.listeners[sensorId]) return;

    this.listeners[sensorId] = this.listeners[sensorId].filter((cb) => cb !== callback);

    if (this.listeners[sensorId].length === 0) {
      delete this.listeners[sensorId];
    }

    if (Object.keys(this.listeners).length === 0) {
      this.stopStreaming();
    }
  }

  /**
   * Internally initiates data streaming if not already active.
   * Listeners receive sensor updates as they arrive.
   * @private
   * @async
   */
  private async startStreaming(): Promise<void> {
    if (this.isStreaming) return;
    this.isStreaming = true;

    // TODO: Mock Implementation, will look at this once we implement data...
    // Primary goal is to show the point of this service, we want to keep logic out of the components
    // While keeping the implementation details out of the react hooks.
    this.plugin.addListener('onStreamPacketReceived', (event) => {
      const { macAddress, accelerometer, gyroscope, quaternion } = event;

      // Update sensor state
      if (!this.sensors[macAddress]) {
        this.sensors[macAddress] = { id: macAddress, batteryLevel: 100, movementHistory: Array(20).fill(0) };
      }

      // Shift movement buffer
      this.sensors[macAddress].movementHistory = [...this.sensors[macAddress].movementHistory.slice(1), accelerometer];

      // Notify listeners
      if (this.listeners[macAddress]) {
        this.listeners[macAddress].forEach((cb) => cb(macAddress, this.sensors[macAddress]));
      }
    });

    this.plugin.addListener('onBatteryUpdate', (event) => {
      const { macAddress, battery } = event;

      // Update sensor state
      if (!this.sensors[macAddress]) {
        this.sensors[macAddress] = { id: macAddress, batteryLevel: 100, movementHistory: Array(20).fill(0) };
      }

      this.sensors[macAddress].batteryLevel = battery;

      // Notify listeners
      if (this.listeners[macAddress]) {
        this.listeners[macAddress].forEach((cb) => cb(macAddress, this.sensors[macAddress]));
      }
    });

    await this.plugin.startStreaming();
  }

  /**
   * Used to stop streaming when there is an active stream and this function is called.
   * @private
   * @async
   */
  private async stopStreaming(): Promise<void> {
    if (!this.isStreaming) return;
    this.isStreaming = false;
    await this.plugin.stopStreaming();
  }

  /**
   * Retrieves immediate data for a given sensor, based on the specified data type.
   * @async
   * @param sensorId - The sensor identifier.
   * @param dataType - The type of data to retrieve (e.g., accelerometer, gyroscope, etc.).
   * @returns The requested data, as provided by the plugin.
   */
  async getSensorData(sensorId: string, dataType: dataType): Promise<{ data: any }> {
    return this.plugin.getData({ macAddress: sensorId, dataType });
  }
}

export const sensorService = new SensorService(NativeQSenseControl);
